import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Layers, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitPlatform() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!name.trim()) { toast.error('Platform name required'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/platforms/submit`, { name, description, website }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Platform submitted for review!');
      navigate('/platforms');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="submit-platform-page">
      <Card className="border-border/50 shadow-xl">
        <CardHeader>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
            <Layers className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Submit a Platform</CardTitle>
          <CardDescription>Suggest a new AI platform for the community. It will be reviewed by moderators before approval.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="submit-platform-form">
            <div className="space-y-2">
              <Label htmlFor="name">Platform Name</Label>
              <Input id="name" placeholder="e.g. Perplexity AI" value={name} onChange={(e) => setName(e.target.value)} required className="h-11" data-testid="platform-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" placeholder="What does this platform do?" value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-[100px]" data-testid="platform-desc-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website URL</Label>
              <Input id="website" placeholder="https://example.com" value={website} onChange={(e) => setWebsite(e.target.value)} className="h-11" data-testid="platform-website-input" />
            </div>
            <Button type="submit" className="w-full h-11 rounded-full shadow-lg shadow-primary/20 gap-2" disabled={loading} data-testid="submit-platform-btn">
              {loading ? 'Submitting...' : 'Submit for Review'} <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
